import CallDesk from "./components/CallDesk";
import { available as unitAvailable } from "./domain/availability";
import { incidentCode } from "./domain/incidentProfiles";
import { useEffect, useRef, useState } from "react";
import {
  Activity,
  ArrowDownToLine,
  ArrowUpRight,
  BarChart3,
  BellRing,
  Building2,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  Coins,
  FileText,
  Flame,
  Globe2,
  Map,
  MapPin,
  Monitor,
  Pause,
  PhoneIncoming,
  Play,
  Radio,
  Search,
  Settings2,
  Shield,
  Siren,
  Truck,
  X,
  Volume2,
  VolumeX,
} from "lucide-react";
import { command, request } from "./api";
import {
  type Command,
  type Incident,
  type Point,
  type SimState,
  isOpen,
  phaseLabels,
  incidentLabels,
  IncidentType,
} from "./domain/model";
import { osm, type MapProvider } from "./providers/maps";
import type { CameraView, SimCamFrame, SimCamStatus } from "./providers/simcam";
import DispatchMap, { type MapFocus } from "./components/DispatchMap";
import IncidentInspector from "./components/IncidentInspector";
import FleetPanel from "./components/FleetPanel";
import GermanyPanel from "./components/GermanyPanel";
import type { CatalogEntry } from "./domain/catalog";
import type { ControlCenter } from "./domain/controlCenters";
import Modal from "./components/Modal";
import ResourceManager from "./components/ResourceManager";
import Settings from "./components/Settings";
import DisplaySetup from "./components/DisplaySetup";
import CallComposer from "./components/CallComposer";
import { registerSimulationTools } from "./webmcp";
import { time } from "./format";
import RadioPanel from "./components/RadioPanel";
import RadioDock from "./components/RadioDock";
import { useRadioAudio } from "./audio/useRadioAudio";
import type { CreditTransaction, Player } from "./domain/player";

export function Brand({ link = false }: { link?: boolean }) {
  const content = (
    <>
      <svg
        className="brand-symbol"
        width="47"
        height="43"
        viewBox="0 0 47 43"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="metal" x1="0" x2="1" y1="0" y2="1">
            <stop stopColor="#aebdc7" />
            <stop offset="1" stopColor="#455966" />
          </linearGradient>
        </defs>
        <path
          d="M8 7 5 34M20 7 14 33"
          stroke="url(#metal)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <path
          d="M21 17 24 35 39 7"
          fill="none"
          stroke="#d3263c"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>
        LEITWERK
        <small>
          INTEGRIERTE LEITSTELLE <i />
        </small>
      </span>
    </>
  );
  return link ? (
    <a className="leitwerk-brand" href="/" aria-label="Zur LEITWERK-Startseite">
      {content}
    </a>
  ) : (
    <div className="leitwerk-brand">{content}</div>
  );
}
function TypeIcon({ type }: { type: Incident["type"] }) {
  return type === "brand" ? <Flame size={19} /> : <Siren size={19} />;
}
function App({
  player,
  onPlayer,
  onLogout,
}: {
  player: Player;
  onPlayer: (player: Player) => void;
  onLogout: () => Promise<void>;
}) {
  const [state, setState] = useState<SimState | null>(null),
    [connected, setConnected] = useState(false),
    [storageError, setStorageError] = useState<string | null>(null),
    [selectedId, setSelectedId] = useState(""),
    [view, setView] = useState("lage"),
    [filter, setFilter] = useState("offen"),
    [search, setSearch] = useState(""),
    [globalSearch, setGlobalSearch] = useState(""),
    [selectedVehicles, setSelectedVehicles] = useState<string[]>([]),
    [toast, setToast] = useState(""),
    [busy, setBusy] = useState(false),
    [mapProvider, setMapProvider] = useState<MapProvider>(osm),
    [dialog, setDialog] = useState<string | null>(null),
    [camView, setCamView] = useState<CameraView>("traffic"),
    [cam, setCam] = useState<SimCamFrame | null>(null),
    [camStatus, setCamStatus] = useState<SimCamStatus | null>(null),
    [camBusy, setCamBusy] = useState(false),
    [focus, setFocus] = useState<MapFocus | null>(null),
    [catalogVisible, setCatalogVisible] = useState(false),
    [catalogEntry, setCatalogEntry] = useState<CatalogEntry | null>(null),
    [callPoint, setCallPoint] = useState<Point | null>(null);
  const searchRef = useRef<HTMLInputElement>(null),
    toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null),
    currentState = useRef(state);
  currentState.current = state;
  const [externalMap, setExternalMap] = useState(() => {
    try {
      return (
        localStorage.getItem("leitwerk-external-map-" + player.id) === "true"
      );
    } catch {
      return false;
    }
  });
  const lastDisplayFocus = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    try {
      localStorage.setItem(
        "leitwerk-external-map-" + player.id,
        String(externalMap),
      );
    } catch {}
  }, [externalMap, player.id]);
  useEffect(() => {
    if (!connected) {
      lastDisplayFocus.current = undefined;
      return;
    }
    if (!state) return;
    const id =
      (
        state.incidents.find((i) => i.id === selectedId) ??
        state.incidents.find(isOpen) ??
        state.incidents[0]
      )?.id ?? null;
    if (lastDisplayFocus.current === id) return;
    const timer = setTimeout(() => {
      lastDisplayFocus.current = id;
      void request("display/focus", { incidentId: id }).catch(() => {
        lastDisplayFocus.current = undefined;
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [state, selectedId, connected]);
  const audio = useRadioAudio(state, connected, player.id);
  useEffect(
    () =>
      registerSimulationTools(
        () => currentState.current,
        (s) =>
          setState((current) =>
            !current || s.revision >= current.revision ? s : current,
          ),
      ),
    [],
  );
  useEffect(() => {
    const hotkey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") setGlobalSearch("");
    };
    window.addEventListener("keydown", hotkey);
    return () => window.removeEventListener("keydown", hotkey);
  }, []);
  function notify(message: string) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 5500);
  }
  useEffect(() => {
    const events = new EventSource("/api/events");
    events.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setState((current) =>
        !current || data.state.revision >= current.revision
          ? data.state
          : current,
      );
      setStorageError(data.storageError);
      if (data.player) onPlayer(data.player);
      setConnected(true);
    };
    events.onerror = () => setConnected(false);
    request<{ map: MapProvider | null }>("config")
      .then((c) => {
        if (c.map) setMapProvider(c.map);
      })
      .catch(() => {});
    return () => events.close();
  }, [onPlayer]);
  useEffect(() => {
    setSelectedVehicles([]);
    setCam(null);
    setCamStatus(null);
  }, [selectedId]);
  useEffect(() => {
    if (!state) return;
    setSelectedVehicles((ids) => {
      const valid = ids.filter((id) =>
        state.vehicles.some((v) => v.id === id && unitAvailable(v, state.now)),
      );
      return valid.length === ids.length ? ids : valid;
    });
  }, [state]);
  async function act(c: Command, message?: string) {
    setBusy(true);
    try {
      await audio.prepare();
      const result = await command(c);
      setState((current) =>
        !current || result.state.revision >= current.revision
          ? result.state
          : current,
      );
      onPlayer(result.player);
      if (result.transaction?.amount && result.transaction.amount > 0)
        notify(
          `Einsatz abgeschlossen · +${result.transaction.amount.toLocaleString("de-DE")} Credits`,
        );
      else if (message) notify(message);
      return result.state;
    } catch (error) {
      notify((error as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }
  if (!state)
    return (
      <div className="loading">
        <Brand link />
        <p>Verbindung zur Leitstelle wird hergestellt …</p>
        <small>
          Bitte prüfe deine Internetverbindung, falls diese Meldung bestehen
          bleibt.
        </small>
      </div>
    );
  const selected =
    state.incidents.find((i) => i.id === selectedId) ??
    state.incidents.find(isOpen) ??
    state.incidents[0];
  const open = state.incidents.filter(isOpen),
    available = state.vehicles.filter((v) => unitAvailable(v, state.now)),
    working = connected && !busy;
  const incidents = state.incidents.filter(
    (i) =>
      (filter === "archiv"
        ? !isOpen(i)
        : filter === "neu"
          ? i.phase === "gemeldet"
          : isOpen(i)) &&
      `${i.number} ${i.title} ${i.address}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const journal = state.incidents
    .flatMap((i) => i.timeline.map((e) => ({ ...e, incident: i })))
    .sort((a, b) => b.at - a.at);
  function select(id: string) {
    setSelectedId(id);
    setView("lage");
    setGlobalSearch("");
  }
  function focusPoint(point: Point, zoom?: number) {
    if (externalMap) {
      const incident = state?.incidents.find(
        (item) =>
          item.location[0] === point[0] && item.location[1] === point[1],
      );
      if (incident) {
        void request("display/focus", { incidentId: incident.id })
          .then(() =>
            notify(
              "Einsatz an Kartenbildschirm gesendet. Dort „PC-Auswahl folgen“ aktivieren.",
            ),
          )
          .catch((error) => notify(error.message));
        setView("lage");
        return;
      }
      setExternalMap(false);
    }
    setFocus({ point, sequence: Date.now(), zoom });
    setView("lage");
  }
  async function loadCamStatus(incidentId: string) {
    setCamBusy(true);
    try {
      const status = await request<SimCamStatus>(
        `simcam?incidentId=${encodeURIComponent(incidentId)}`,
      );
      setCamStatus(status);
      if (status.frames[0]) setCam(status.frames[0]);
    } catch (error) {
      notify((error as Error).message);
    } finally {
      setCamBusy(false);
    }
  }
  function openCam() {
    if (!selected) return;
    setDialog("cam");
    void loadCamStatus(selected.id);
  }
  async function activateCatalog(ids: string[], equip: boolean) {
    setBusy(true);
    try {
      const result = await request<{
        state: SimState;
        player: Player;
        transaction?: CreditTransaction;
      }>("catalog/activate", { ids, equip });
      setState((current) =>
        !current || result.state.revision >= current.revision
          ? result.state
          : current,
      );
      onPlayer(result.player);
      notify(
        result.transaction
          ? `${Math.abs(result.transaction.amount).toLocaleString("de-DE")} Credits investiert. Standorte und Fahrzeuge ergänzt.`
          : "Standorte übernommen. Ergänzte Fahrzeuge sind als Simulation gekennzeichnet.",
      );
      return true;
    } catch (e) {
      notify((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function selectControlCenter(id: string) {
    setBusy(true);
    try {
      const result = await request<{
        state: SimState;
        entry: ControlCenter;
        resources: { areaStations: number; areaVehicles: number };
        player: Player;
      }>("control-centers/select", { id });
      setState((current) =>
        !current || result.state.revision >= current.revision
          ? result.state
          : current,
      );
      onPlayer(result.player);
      notify(
        `${result.entry.name} übernommen: ${result.resources.areaStations} Standorte und ${result.resources.areaVehicles} simulierte Einheiten im Einsatzbereich verfügbar.`,
      );
      return true;
    } catch (error) {
      notify((error as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function dispatch() {
    if (!selected) return;
    const result = await act(
      {
        type: "dispatch",
        incidentId: selected.id,
        vehicleIds: selectedVehicles,
      },
      `${selectedVehicles.length} Kräfte alarmiert.`,
    );
    if (result) {
      setSelectedVehicles([]);
      setDialog(null);
      setView("lage");
    }
  }
  const query = globalSearch.trim().toLowerCase();
  const results = query
    ? [
        ...state.incidents
          .filter((i) =>
            `${i.number} ${i.address} ${i.title} ${i.district}`
              .toLowerCase()
              .includes(query),
          )
          .map((i) => ({
            id: i.id,
            label: i.address,
            detail: `E ${i.number} · ${i.title}`,
            kind: "incident",
            point: i.location,
          })),
        ...state.vehicles
          .filter((v) => `${v.name} ${v.kind}`.toLowerCase().includes(query))
          .map((v) => ({
            id: v.id,
            label: v.name,
            detail: `Fahrzeug · ${v.kind}`,
            kind: "vehicle",
            point: v.location,
          })),
        ...state.stations
          .filter((w) => w.name.toLowerCase().includes(query))
          .map((w) => ({
            id: w.id,
            label: w.name,
            detail: `Wache · ${w.service}`,
            kind: "station",
            point: w.location,
          })),
      ].slice(0, 8)
    : [];
  const nav = [
    { id: "lage", label: "Lage", icon: Map },
    { id: "incidents", label: "Einsätze", icon: ClipboardList },
    { id: "fleet", label: "Fahrzeuge", icon: Truck },
    { id: "catalog", label: "Deutschland", icon: Globe2 },
    { id: "resources", label: "Wachen", icon: MapPin },
    { id: "log", label: "Meldungen", icon: FileText },
    { id: "stats", label: "Statistik", icon: BarChart3 },
  ];
  return (
    <div className="application with-radio-dock">
      <header className="topbar">
        <Brand link />
        <div className="workspace-location">
          <button
            className="area-trigger"
            onClick={() => setView("catalog")}
            title="Einsatzgebiet ändern"
          >
            <b>
              {state.area.name} <ChevronDown size={13} />
            </b>
          </button>
          <small>
            {state.area.controlCenterId
              ? `${state.area.federalState} · ${state.area.jurisdictions?.length ?? 1} Zuständigkeitsgebiet${(state.area.jurisdictions?.length ?? 1) === 1 ? "" : "e"}`
              : `Deutschland · ${state.area.radiusKm} km freier Übungsbereich`}
          </small>
        </div>
        <div className="global-search">
          <Search size={17} />
          <input
            ref={searchRef}
            aria-label="Arbeitsplatz durchsuchen"
            placeholder="Adresse, Fahrzeug oder Einsatz suchen …"
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
          />
          {globalSearch ? (
            <button
              aria-label="Suche leeren"
              onClick={() => setGlobalSearch("")}
            >
              <X size={16} />
            </button>
          ) : (
            <kbd>⌘ K</kbd>
          )}
          {query && (
            <div className="search-results">
              {results.length ? (
                results.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setGlobalSearch("");
                      if (r.kind === "incident") {
                        select(r.id);
                        focusPoint(r.point);
                      } else if (r.kind === "station") {
                        focusPoint(r.point);
                      } else {
                        const v = state.vehicles.find((v) => v.id === r.id);
                        if (v?.incidentId) select(v.incidentId);
                        setView("fleet");
                      }
                    }}
                  >
                    <span className="result-icon">
                      {r.kind === "incident" ? (
                        <ClipboardList size={16} />
                      ) : r.kind === "vehicle" ? (
                        <Truck size={16} />
                      ) : (
                        <Building2 size={16} />
                      )}
                    </span>
                    <span>
                      {r.label}
                      <small>{r.detail}</small>
                    </span>
                    <ArrowUpRight size={14} />
                  </button>
                ))
              ) : (
                <p>Keine passenden Einsätze, Wachen oder Fahrzeuge.</p>
              )}
            </div>
          )}
        </div>
        <div className="simulation-clock">
          <time>{time(state.now)}</time>
          <small>
            {new Date(state.now).toLocaleDateString("de-DE", {
              weekday: "short",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </small>
        </div>
        <div className="simulation-control">
          <button
            className={`simulation-toggle ${state.running ? "running" : "paused"}`}
            disabled={!working}
            onClick={() => act({ type: "settings", running: !state.running })}
            title={
              state.running ? "Simulation pausieren" : "Simulation fortsetzen"
            }
            aria-label={
              state.running ? "Simulation pausieren" : "Simulation fortsetzen"
            }
          >
            {state.running ? <i className="live-dot" /> : <Pause size={18} />}
            <span>
              {state.running ? "Simulation aktiv" : "Simulation pausiert"}
              <small>
                {state.running ? "Zeitsteuerung" : "Zeit steht still"}
              </small>
            </span>
            <span className="play-icon">
              {state.running ? <Pause size={13} /> : <Play size={13} />}
            </span>
          </button>
          <select
            aria-label="Simulationsgeschwindigkeit"
            disabled={!working}
            value={state.speed}
            onChange={(e) =>
              act({
                type: "settings",
                speed: Number(e.target.value) as 1 | 5 | 20,
              })
            }
          >
            <option value={1}>1×</option>
            <option value={5}>5×</option>
            <option value={20}>20×</option>
          </select>
        </div>
        <button
          className="credit-pill"
          onClick={() => setView("resources")}
          title="Credits und Ausbau öffnen"
        >
          <Coins size={16} />
          <span>
            <b>{player.credits.toLocaleString("de-DE")}</b>
            <small>Credits</small>
          </span>
        </button>
        <button
          className="avatar"
          aria-label="Arbeitsplatz-Einstellungen öffnen"
          onClick={() => setDialog("settings")}
        >
          {player.username.slice(0, 2).toUpperCase()}
        </button>
      </header>
      <aside className="sidebar">
        <nav aria-label="Hauptnavigation">
          {nav.map((n) => (
            <button
              key={n.id}
              className={view === n.id ? "active" : ""}
              aria-current={view === n.id ? "page" : undefined}
              onClick={() => {
                setView(n.id);
                setGlobalSearch("");
              }}
            >
              <n.icon size={21} />
              <span>{n.label}</span>
              {n.id === "incidents" && open.length > 0 && <b>{open.length}</b>}
            </button>
          ))}
          <button onClick={() => setDialog("display")}>
            <Monitor size={21} />
            <span>Kartenbildschirm</span>
          </button>
          <button onClick={() => setDialog("settings")}>
            <Settings2 size={21} />
            <span>Einstellungen</span>
          </button>
        </nav>
        <div className="sidebar-bottom">
          <button
            className="new-call"
            disabled={!working}
            onClick={() => {
              setCallPoint(null);
              setDialog("call");
            }}
          >
            <PhoneIncoming size={18} />
            <span>Einsatz manuell</span>
          </button>
          <button className="help-link" onClick={() => setDialog("help")}>
            <CircleHelp size={16} />
            <span>Bedienhilfe</span>
          </button>
          <span className="version">
            LEITWERK <small>112BASE</small>
          </span>
          <span className={`connection ${connected ? "" : "disconnected"}`}>
            <i />
            {connected ? "Mit Server verbunden" : "Verbindung getrennt"}
          </span>
        </div>
      </aside>
      <main
        className={"map-workspace" + (externalMap ? " external-map-desk" : "")}
        aria-label="Lageübersicht"
      >
        {!externalMap && (
          <DispatchMap
            state={state}
            selectedId={selected?.id ?? ""}
            onSelect={select}
            provider={mapProvider}
            focus={focus}
            onVehicleSelect={() => setView("fleet")}
            catalogVisible={catalogVisible}
            onCatalogToggle={() => setCatalogVisible(!catalogVisible)}
            onCatalogSelect={(entry) => {
              setCatalogEntry(entry);
              setDialog("catalogStation");
            }}
            onCreateIncident={(point) => {
              setCallPoint(point);
              setDialog("call");
            }}
          />
        )}
        {(!connected || storageError) && (
          <div className="connection-banner" role="alert">
            {storageError
              ? `Speichern fehlgeschlagen: ${storageError}`
              : "Verbindung unterbrochen. Änderungen sind derzeit nicht möglich."}
          </div>
        )}
        {(view === "incidents" || (externalMap && view === "lage")) && (
          <section className="incidents-drawer">
            <div className="panel-heading">
              <div>
                <h2>Einsätze</h2>
                <p>{open.length} aktive Lagen im Einsatzgebiet</p>
              </div>
              {!externalMap && (
                <button
                  aria-label="Einsatzliste schließen"
                  onClick={() => setView("lage")}
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <div className="incident-filters">
              {[
                ["offen", "Aktiv"],
                ["neu", "Neu"],
                ["archiv", "Archiv"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  aria-pressed={filter === v}
                  className={filter === v ? "selected" : ""}
                  onClick={() => setFilter(v)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="search-field">
              <Search size={15} />
              <input
                aria-label="Einsätze durchsuchen"
                placeholder="Einsatz oder Adresse filtern …"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="incident-list">
              {incidents.map((i) => (
                <button
                  key={i.id}
                  className={`incident-row ${i.id === selected?.id ? "selected" : ""}`}
                  onClick={() => select(i.id)}
                >
                  <div className="incident-row-top">
                    <span className={`mini-type ${i.type}`}>
                      <TypeIcon type={i.type} />
                    </span>
                    <b>
                      {incidentCode(i)} · {i.title}
                    </b>
                    <time>{time(i.createdAt).slice(0, 5)}</time>
                  </div>
                  <div className="incident-row-address">
                    {i.address}
                    <small>{i.district}</small>
                  </div>
                  <div className="incident-row-bottom">
                    <span>E {i.number}</span>
                    <span className={`phase ${i.phase}`}>
                      <i />
                      {phaseLabels[i.phase]}
                    </span>
                    <ChevronRight size={13} />
                  </div>
                </button>
              ))}
              {!incidents.length && (
                <div className="empty">Keine Einsätze in dieser Ansicht.</div>
              )}
            </div>
            <button
              className="button secondary"
              disabled={!working}
              onClick={() => {
                setCallPoint(null);
                setDialog("call");
              }}
            >
              <PhoneIncoming size={16} />
              Neuen Notruf aufnehmen
            </button>
          </section>
        )}
        {["fleet", "resources", "log", "stats", "catalog"].includes(view) && (
          <section
            className={`workspace-panel ${view === "fleet" ? "fleet-workspace" : ""}`}
          >
            <div className="panel-heading">
              <div>
                <span className="eyebrow">LEITSTELLENARBEITSPLATZ</span>
                <h2>
                  {view === "catalog"
                    ? "Leitstelle & Deutschland"
                    : view === "fleet"
                      ? "Fahrzeuge"
                      : view === "resources"
                        ? "Wachen & Ressourcen"
                        : view === "log"
                          ? "Meldungsjournal"
                          : "Betriebsübersicht"}
                </h2>
              </div>
              <button
                className="close-panel"
                aria-label="Zur Lagekarte"
                onClick={() => setView("lage")}
              >
                <Map size={16} />
                <span>Zur Karte</span>
                <X size={16} />
              </button>
            </div>
            {view === "catalog" ? (
              <GermanyPanel
                state={state}
                act={act}
                enabled={working}
                onActivate={activateCatalog}
                onFocus={(point) => {
                  setCatalogVisible(true);
                  focusPoint(point, 15);
                }}
                onGermany={() => {
                  setCatalogVisible(true);
                  focusPoint([51.16, 10.45], 6);
                }}
                onAreaMap={() => {
                  focusPoint(
                    state.area.center,
                    state.area.radiusKm < 35
                      ? 10
                      : state.area.radiusKm < 70
                        ? 9
                        : 8,
                  );
                }}
                onSelectControlCenter={selectControlCenter}
              />
            ) : view === "fleet" ? (
              <FleetPanel
                act={act}
                onState={(next) =>
                  setState((current) =>
                    !current || next.revision >= current.revision
                      ? next
                      : current,
                  )
                }
                state={state}
                incident={selected}
                selected={selectedVehicles}
                onSelected={setSelectedVehicles}
                enabled={working}
                onDispatch={dispatch}
                onRequestStatus={(vehicleId) => {
                  void act(
                    { type: "requestStatus", vehicleId },
                    "Statusabfrage im Funkprotokoll.",
                  );
                }}
              />
            ) : view === "resources" ? (
              <ResourceManager
                state={state}
                act={act}
                enabled={working}
                player={player}
                onPlayer={onPlayer}
                onState={setState}
                notify={notify}
              />
            ) : view === "log" ? (
              <div className="journal-list">
                {journal.length ? (
                  journal.map((e) => (
                    <button
                      className="journal-row"
                      key={`${e.incident.id}-${e.id}`}
                      onClick={() => select(e.incident.id)}
                    >
                      <time>{time(e.at)}</time>
                      <b>E {e.incident.number}</b>
                      <span>{e.text}</span>
                      <ChevronRight size={16} />
                    </button>
                  ))
                ) : (
                  <div className="empty">Noch keine Meldungen vorhanden.</div>
                )}
              </div>
            ) : (
              <div className="statistics">
                <p className="muted">
                  Aktueller Stand der lokalen Übung · {time(state.now)}
                </p>
                <div className="stats-grid">
                  {[
                    ["Aktive Einsätze", open.length],
                    [
                      "Abgeschlossene Einsätze",
                      state.incidents.filter((i) => !isOpen(i)).length,
                    ],
                    [
                      "Kräfte verfügbar",
                      `${available.length} / ${state.vehicles.length}`,
                    ],
                    ["Wachen", state.stations.length],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <h3>Aktive Einsatzlagen</h3>
                {IncidentType.options.map((t) => (
                  <div className="stats-bar" key={t}>
                    <span>{incidentLabels[t].title}</span>
                    <div>
                      <i
                        style={{
                          width: `${open.length ? (open.filter((i) => i.type === t).length / open.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <b>{open.filter((i) => i.type === t).length}</b>
                  </div>
                ))}
                <h3>Fahrzeugstatus</h3>
                {[
                  [2, "Frei auf Wache"],
                  [1, "Frei über Funk / Rückfahrt"],
                  [3, "Auf Anfahrt"],
                  [4, "An der Einsatzstelle"],
                  [6, "Außer Dienst"],
                ].map(([s, label]) => (
                  <div className="stats-bar" key={s}>
                    <span>{label}</span>
                    <div>
                      <i
                        style={{
                          width: `${state.vehicles.length ? (state.vehicles.filter((v) => v.status === s).length / state.vehicles.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <b>{state.vehicles.filter((v) => v.status === s).length}</b>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
      <IncidentInspector
        state={state}
        incident={selected}
        act={act}
        enabled={working}
        onFocus={() => selected && focusPoint(selected.location)}
        onDispatch={() => setDialog("dispatch")}
        onCam={openCam}
        onMeasures={() => setDialog("measures")}
        onRadio={() =>
          document
            .querySelector(".radio-dock")
            ?.scrollIntoView({ block: "nearest" })
        }
      />
      <footer className="overview-strip">
        <button onClick={() => setView("incidents")}>
          <strong>{open.length}</strong>
          <span>Aktive Einsätze</span>
        </button>
        <button onClick={() => setView("fleet")}>
          <strong>
            {available.length}
            <em> / {state.vehicles.length}</em>
          </strong>
          <span>Kräfte verfügbar</span>
        </button>
        <button onClick={() => setView("fleet")}>
          <strong>{state.vehicles.filter((v) => v.status === 3).length}</strong>
          <span>Auf Anfahrt</span>
        </button>
        <button onClick={() => setView("fleet")}>
          <strong>{state.vehicles.filter((v) => v.status === 4).length}</strong>
          <span>Vor Ort</span>
        </button>
        <button
          onClick={() => {
            setFilter("archiv");
            setView("incidents");
          }}
        >
          <strong>{state.incidents.filter((i) => !isOpen(i)).length}</strong>
          <span>Abgeschlossen</span>
        </button>
      </footer>
      <div
        className={
          "radio-strip radio-console" +
          (state.speechRequests.length ? " has-speech-request" : "")
        }
      >
        <button
          className="radio-open"
          onClick={() => {
            void audio.prepare();
            setDialog("radio");
          }}
        >
          <Radio size={17} />
          <i className={connected ? "live-dot" : "offline-dot"} />
          <span>
            <b>Funkprotokoll & Audio</b>
            <small>
              {audio.runtime.speaking
                ? "Durchsage läuft"
                : !audio.preferences.enabled
                  ? "Funk stummgeschaltet"
                  : !audio.preferences.volume
                    ? "Lautstärke 0 %"
                    : !audio.preferences.speech
                      ? "Sprachausgabe ausgeschaltet"
                      : !audio.runtime.unlocked
                        ? "Ton noch nicht freigegeben"
                        : audio.runtime.error
                          ? "Audioproblem · Details öffnen"
                          : `${state.radioMessages.length} Meldungen`}
            </small>
          </span>
          <ChevronRight size={16} />
        </button>
        <button
          className="radio-toggle"
          aria-label={
            audio.runtime.unlocked && audio.preferences.enabled
              ? "Funk stummschalten"
              : "Funk aktivieren"
          }
          title={
            audio.runtime.unlocked && audio.preferences.enabled
              ? "Funk stummschalten"
              : "Ton und Sprachausgabe aktivieren"
          }
          onClick={() =>
            audio.runtime.unlocked && audio.preferences.enabled
              ? audio.change({ enabled: false })
              : void audio.activate()
          }
        >
          {audio.runtime.unlocked && audio.preferences.enabled ? (
            <Volume2 size={19} />
          ) : (
            <VolumeX size={19} />
          )}
          <small>
            {audio.runtime.unlocked && audio.preferences.enabled
              ? "Ton an"
              : "Aktivieren"}
          </small>
        </button>
      </div>
      <CallDesk
        state={state}
        act={act}
        enabled={working}
        audio={audio}
        onIncident={(id, point) => {
          select(id);
          focusPoint(point, 14);
        }}
      />
      <RadioDock
        state={state}
        act={act}
        enabled={working}
        audio={audio}
        onIncident={(id) => {
          setSelectedId(id);
          setView("map");
        }}
      />
      {toast && (
        <div role="status" className="toast">
          <BellRing size={16} />
          {toast}
          <button aria-label="Meldung schließen" onClick={() => setToast("")}>
            <X size={15} />
          </button>
        </div>
      )}
      {dialog && (
        <Modal
          title={
            dialog === "display"
              ? "Karte auf einem zweiten Bildschirm"
              : dialog === "radio"
                ? "Funkarbeitsplatz"
                : dialog === "catalogStation"
                  ? "Standort aus Deutschlandkatalog"
                  : dialog === "dispatch"
                    ? "Kräfte alarmieren"
                    : dialog === "call"
                      ? "Manuellen Einsatz anlegen"
                      : dialog === "cam"
                        ? "SimCam · Lagebild"
                        : dialog === "measures"
                          ? "Maßnahmen veranlassen"
                          : dialog === "settings"
                            ? "Arbeitsplatz-Einstellungen"
                            : "Willkommen bei LEITWERK"
          }
          onClose={() => setDialog(null)}
        >
          {dialog === "display" ? (
            <DisplaySetup
              externalMap={externalMap}
              onExternalMap={setExternalMap}
            />
          ) : dialog === "radio" ? (
            <RadioPanel
              state={state}
              act={act}
              enabled={working}
              audio={audio}
            />
          ) : dialog === "catalogStation" && catalogEntry ? (
            <div className="dialog-stack">
              <h3>{catalogEntry.name}</h3>
              <p className="muted">
                {catalogEntry.organization || catalogEntry.stationType} ·{" "}
                {catalogEntry.service}
                <br />
                {[
                  catalogEntry.address,
                  catalogEntry.postcode,
                  catalogEntry.city,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <a
                href={catalogEntry.source.url}
                target="_blank"
                rel="noreferrer"
              >
                Standort in OpenStreetMap prüfen ↗
              </a>
              <p className="muted small">
                Öffentlich kartierter Standort. Ein ergänztes Fahrzeugpaket ist
                vollständig simuliert; reale Fahrzeuge und Bereitschaft werden
                nicht behauptet.
              </p>
              <button
                className="button primary"
                disabled={!working}
                onClick={async () => {
                  if (await activateCatalog([catalogEntry.id], true))
                    setDialog(null);
                }}
              >
                Standort mit Simulationsfahrzeugen übernehmen
              </button>
              <button
                className="button secondary"
                onClick={async () => {
                  if (
                    await act(
                      {
                        type: "setArea",
                        area: {
                          name:
                            catalogEntry.city || catalogEntry.name.slice(0, 80),
                          center: catalogEntry.location,
                          radiusKm: 15,
                        },
                      },
                      "Einsatzgebiet übernommen.",
                    )
                  ) {
                    setDialog(null);
                    focusPoint(catalogEntry.location, 12);
                  }
                }}
              >
                Einsatzgebiet hier festlegen
              </button>
            </div>
          ) : dialog === "dispatch" ? (
            <FleetPanel
              act={act}
              onState={(next) =>
                setState((current) =>
                  !current || next.revision >= current.revision
                    ? next
                    : current,
                )
              }
              state={state}
              incident={selected}
              selected={selectedVehicles}
              onSelected={setSelectedVehicles}
              enabled={working}
              onDispatch={dispatch}
              onRequestStatus={(vehicleId) => {
                void act(
                  { type: "requestStatus", vehicleId },
                  "Statusabfrage im Funkprotokoll.",
                );
              }}
            />
          ) : dialog === "call" ? (
            <CallComposer
              state={state}
              initialPoint={callPoint}
              act={act}
              enabled={working}
              onCreated={(id, point) => {
                select(id);
                focusPoint(point, 14);
                setDialog(null);
              }}
            />
          ) : dialog === "cam" ? (
            <div className="dialog-stack cam-dialog">
              <p className="muted">
                {selected?.address} · E {selected?.number}
              </p>
              {cam?.imageUrl ? (
                <figure className="cam-surface generated">
                  <img src={cam.imageUrl} alt={cam.caption} />
                  <figcaption>
                    <b>{cam.caption}</b>
                    <span>
                      KI-generiertes Trainingslagebild · kein Livebild
                    </span>
                  </figcaption>
                </figure>
              ) : (
                <div className="cam-surface">
                  <Camera size={38} />
                  <b>
                    {camBusy
                      ? "SimCam wird geladen …"
                      : cam?.status === "prepared"
                        ? "Szenenauftrag vorbereitet"
                        : camStatus?.enabled
                          ? "Noch kein Lagebild vorhanden"
                          : "Kein Bildanbieter verbunden"}
                  </b>
                  <span>
                    {camStatus?.enabled
                      ? "Cloudflare Workers AI · automatisch und manuell"
                      : "Vorbereitete SimCam-Schnittstelle · kein Livebild"}
                  </span>
                </div>
              )}
              {camStatus?.enabled && (
                <>
                  <div className="cam-status-line">
                    <span className="live-dot" />
                    <b>Bildgenerierung aktiv</b>
                    <span>
                      {camStatus.automatic
                        ? "Automatik aktiv"
                        : "Manueller Betrieb"}{" "}
                      · maximal {camStatus.limits.perIncident} je Einsatz /{" "}
                      {camStatus.limits.perDay} in 24 Std.
                    </span>
                  </div>
                  <p className="cam-standards-note">
                    Bildvorgabe: deutsche BOS-Fahrzeuge, blaue Sondersignale,
                    deutsche Einsatzkleidung, Rechtsverkehr und
                    StVO-Beschilderung.
                  </p>
                </>
              )}
              <label>
                Kameraperspektive
                <select
                  value={camView}
                  onChange={(e) => setCamView(e.target.value as CameraView)}
                >
                  <option value="traffic">Verkehrskamera</option>
                  <option value="drone">Drohne / Vogelperspektive</option>
                  <option value="phone">Smartphone</option>
                </select>
              </label>
              <button
                className="button primary"
                disabled={!working || !selected || camBusy}
                onClick={async () => {
                  setCamBusy(true);
                  try {
                    const frame = await request<SimCamFrame>("simcam", {
                      incidentId: selected.id,
                      view: camView,
                    });
                    setCam(frame);
                    if (frame.status === "ready") {
                      setCamStatus((current) =>
                        current
                          ? {
                              ...current,
                              frames: [
                                frame,
                                ...current.frames.filter(
                                  (f) => f.id !== frame.id,
                                ),
                              ],
                            }
                          : current,
                      );
                      notify("Neues KI-Lagebild wurde erstellt.");
                    }
                  } catch (e) {
                    notify((e as Error).message);
                  } finally {
                    setCamBusy(false);
                  }
                }}
              >
                <Camera size={16} />
                {camBusy
                  ? "Lagebild wird erzeugt …"
                  : camStatus?.enabled
                    ? "Neues Lagebild erzeugen"
                    : "Szenenauftrag vorbereiten"}
              </button>
              {camStatus?.enabled && (
                <button
                  className="button secondary"
                  disabled={camBusy || !selected}
                  onClick={() => selected && void loadCamStatus(selected.id)}
                >
                  Gespeicherte Lagebilder aktualisieren
                </button>
              )}
              {cam && (
                <div className="cam-result">
                  <Check size={17} />
                  <div>
                    <b>
                      {cam.status === "ready"
                        ? "Lagebild gespeichert"
                        : "Auftrag bereit"}{" "}
                      · {time(cam.generatedAt)}
                    </b>
                    <p>
                      {cam.status === "ready"
                        ? "Das Bild wurde passend zur simulierten Lage erzeugt und bleibt bei diesem Einsatz verfügbar."
                        : "Lage, Koordinaten, Eskalationsstufe und disponierte Kräfte sind im Szenenauftrag enthalten."}
                    </p>
                    <button
                      className="text-button"
                      onClick={() =>
                        downloadJson(cam.request, "simcam-szenenauftrag.json")
                      }
                    >
                      Szenenauftrag herunterladen <ArrowDownToLine size={13} />
                    </button>
                  </div>
                </div>
              )}
              {!!camStatus?.frames.length && (
                <section
                  className="cam-gallery"
                  aria-label="Gespeicherte Lagebilder"
                >
                  <div className="cam-gallery-title">
                    <b>Gespeicherte Lagebilder</b>
                    <span>{camStatus.frames.length}</span>
                  </div>
                  <div className="cam-gallery-grid">
                    {camStatus.frames.map((frame) => (
                      <button key={frame.id} onClick={() => setCam(frame)}>
                        <img src={frame.imageUrl ?? ""} alt="" />
                        <span>
                          {frame.request.view === "traffic"
                            ? "Verkehrskamera"
                            : frame.request.view === "drone"
                              ? "Drohne"
                              : "Smartphone"}
                        </span>
                        <small>{time(frame.generatedAt)}</small>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : dialog === "measures" ? (
            <div className="dialog-stack">
              <p className="muted">
                Kräfte müssen an der Einsatzstelle sein. Maßnahmen werden durch
                die tatsächlich anwesenden Kräfte bearbeitet.
              </p>
              {(
                [
                  "Absicherung",
                  "Menschenrettung",
                  ...(selected?.type === "brand" ? ["Brandbekämpfung"] : []),
                  "Patientenversorgung",
                  "Erstversorgung",
                  ...(selected?.type === "polizei"
                    ? ["Spezialkräfte einsetzen"]
                    : []),
                  "Technische Unterstützung",
                  "Betreuung",
                ] as Array<Extract<Command, { type: "measure" }>["action"]>
              ).map((action) => (
                <button
                  className="scenario-button"
                  key={action}
                  disabled={!working || selected?.actions.includes(action)}
                  onClick={() =>
                    selected &&
                    act(
                      { type: "measure", incidentId: selected.id, action },
                      `${action} veranlasst.`,
                    )
                  }
                >
                  <Shield size={18} />
                  <span>{action}</span>
                  {selected?.actions.includes(action) && <Check size={16} />}
                </button>
              ))}
            </div>
          ) : dialog === "settings" ? (
            <Settings
              audio={audio}
              state={state}
              act={act}
              enabled={working}
              provider={`Luftbild: LGL Baden-Württemberg · Karte: ${mapProvider.name}`}
              onState={(s) => {
                setState(s);
                setSelectedId(s.incidents[0]?.id ?? "");
                setSelectedVehicles([]);
                setCam(null);
              }}
              notify={notify}
              player={player}
              onPlayer={onPlayer}
              onLogout={onLogout}
            />
          ) : (
            <div className="dialog-stack">
              <p>
                1. In der Notrufannahme einen Anruf annehmen, Fragen anklicken
                und Einsatz anlegen.
              </p>
              <p>2. Passende freie Fahrzeuge markieren und alarmieren.</p>
              <p>
                3. Anfahrt verfolgen und nach Eintreffen Maßnahmen veranlassen.
              </p>
              <p>
                4. Patiententransporte und Übergaben abwarten; nach
                Stabilisierung den Einsatz abschließen.
              </p>
              <p className="muted">
                Status 1: frei über Funk · 2: auf Wache · 3: Anfahrt · 4:
                Einsatzstelle · 6: außer Dienst · 7: Patiententransport · 8:
                Krankenhausübergabe. Über die Zeitsteuerung oben lässt sich die
                Simulation pausieren oder beschleunigen.
              </p>
              <p className="muted">
                Trainingssimulation mit vereinfachten Abläufen. Keine Software
                für den realen Leitstellenbetrieb.
              </p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
export function downloadJson(value: unknown, name: string) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export default App;
